import { useMemo, useState } from 'react';
import { InlineStack, Select } from '@customerio/pluma-components/react';
import { fieldError } from '@customerio/dryad';
import { useAuth } from '@/services/auth';
import { hasPlanFeatureEnabled } from '@/services/auth/plan-features';
import { PlanFeature } from '@/services/auth/types';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { useDeliveryFiltersOptional } from '@/services/db/delivery/queries';
import { useBuilderActions, useLeafState, useMeta, useNodeErrors } from '@customerio/dryad-react';
import type { ReactNode } from 'react';
import {
  EMAIL_CHANNEL,
  SEGMENTABLE_ACTION_CHANNELS,
  anyMessageValue,
  messageChannelOf,
  preferredSmsChannel,
  type MessageChannel,
  type SmsChannel,
} from './message-channels';
import { defaultEventTypeFor, eventTypeOptionsFor, withCurrentEventTypeOption } from './message-event-type-options';
import { segmentablePickerData } from './message-picker-data';
import { MessageSelect } from './message-select';

/** The editing state a message leaf holds, as the row reads it. */
export type MessageRowState = {
  eventName: string;
  eventType: string;
  human: boolean;
  inverse: boolean;
};

type MessageRowProps = {
  /** The leaf node's id, supplied by the group to every registered view. */
  id: string;
  /** The plugin type gating the typed read — the surface's message leaf. */
  conditionType: string;
  /** The condition-type select, where the surface offers more than one type. */
  typeSelect?: ReactNode;
  /**
   * Derives a channel from a stored event name. Wire-shaped, so the surface
   * that owns the encoding supplies it.
   */
  channelFromName: (eventName: string) => string;
};

const INVERSE_OPTIONS = [
  { value: 'false', label: 'has ever' },
  { value: 'true', label: 'has never' },
];

const HUMAN_METRIC_OPTIONS = [
  { value: 'human', label: 'by a human' },
  { value: 'human_or_machine', label: 'by a human or machine' },
];

// The messaging actions webhook/twilio/slack count as always available: email
// and webhook are permanent actions, and twilio/slack are enabled by default.
const ALWAYS_ENABLED_ACTIONS = new Set(['webhook', 'twilio', 'slack']);

function isOpenOrClickMetric(eventType: string): boolean {
  return (
    eventType === 'clicked_action' ||
    eventType === 'clicked_email' ||
    eventType === 'opened_action' ||
    eventType === 'opened_email'
  );
}

/**
 * A message condition row: [type] [channel] [message picker] [has (not) ever]
 * [event type] [by a human?]. One row serves every message channel — the
 * channel is picked inside the row and derived from the stored event
 * name/type, never stored itself.
 */
// fallow-ignore-next-line complexity -- the channel/metric coupling rules of the legacy editor, kept in one row
export function MessageRow({ id, conditionType, typeSelect, channelFromName }: MessageRowProps) {
  const meta = useMeta();
  const { state, node } = useLeafState<MessageRowState>(id, conditionType);
  const { updateState } = useBuilderActions();
  const errors = useNodeErrors(id);

  const { account } = useAuth();
  const environment = useEnvironmentOptional();
  const filters = useDeliveryFiltersOptional();
  const legacyTwilioEnabled = useFeatureFlag(AccountFeatureFlag.LegacyTwilio);

  // "Select a message" renders only once the picker has been visited (closing
  // its dropdown) — or from the start when the row mounted with a message
  // already chosen — so a freshly added row doesn't open covered in red.
  const [messageTouched, setMessageTouched] = useState(() => state.eventName !== '');
  const messageErrorMessage = messageTouched ? fieldError(errors, 'eventName') : undefined;

  const enabledActions = environment.data?.environment?.enabled_actions ?? [];
  const emailOpenTrackingEnabled = environment.data?.environment?.email_open_tracking !== false;
  const allowInAppBroadcasts = hasPlanFeatureEnabled(account, PlanFeature.AnonymousInApp);

  const branchMessageTypes = useMemo(
    () => (filters.data == null ? new Map<string, string>() : segmentablePickerData(filters.data).branchMessageTypes),
    [filters.data],
  );

  // Channel derivation mirrors the stored-wire read path: an email event type
  // wins, a language-branch "All Variations" value (output_*) resolves
  // through the branch-type map, and anything else decodes from the
  // message-value grammar. Unresolvable values fall back to email.
  const channel = useMemo(() => {
    const { eventName, eventType } = state;
    if (eventType.includes('email')) {
      return 'email';
    }
    if (eventName.includes('output')) {
      const branchType = branchMessageTypes.get(eventName);
      return branchType != null && messageChannelOf(branchType) != null ? branchType : 'email';
    }
    const derived = channelFromName(eventName);
    return messageChannelOf(derived) != null ? derived : 'email';
  }, [state, branchMessageTypes, channelFromName]);

  // Keep an already-selected SMS action so a saved condition stays editable
  // even after the flag flips; otherwise the two "SMS"-labeled actions
  // collapse to one.
  const smsChannelToShow: SmsChannel =
    channel === 'twilio' || channel === 'sms'
      ? channel
      : preferredSmsChannel({ legacyTwilioEnabled, twilioEnabled: enabledActions.includes('twilio') });

  const channelOptions = useMemo(() => {
    const options: MessageChannel[] = [EMAIL_CHANNEL];
    for (const action of SEGMENTABLE_ACTION_CHANNELS) {
      const isSmsAction = action.value === 'twilio' || action.value === 'sms';
      // The chosen SMS action always renders (SMS is available regardless of
      // configuration); every other action still requires its channel enabled.
      if (isSmsAction) {
        if (action.value !== smsChannelToShow) {
          continue;
        }
      } else if (
        // The saved condition's own channel always renders, even while the
        // environment query is loading or after the workspace disabled the
        // action — dropping it would show a blank select.
        action.value !== channel &&
        !ALWAYS_ENABLED_ACTIONS.has(action.value) &&
        !enabledActions.includes(action.value)
      ) {
        continue;
      }
      // The Ember row flips the twilio label to "SMS" under white_label_twilio,
      // but the catalog label is already "SMS" — a no-op there too, not ported.
      options.push(action);
    }
    return options;
  }, [smsChannelToShow, enabledActions, channel]);

  // Preserved for display only: the raw list stays the validity check in the
  // change handlers below.
  const eventTypeOptions = withCurrentEventTypeOption(
    eventTypeOptionsFor(channel, state.eventName, state.eventType, { emailOpenTrackingEnabled }),
    state.eventType,
  );

  const showHumanMetricSelect = channel === 'email' && isOpenOrClickMetric(state.eventType);

  const patch = (partial: Parameters<typeof updateState>[1]) => {
    if (node != null) {
      updateState(node, partial);
    }
  };

  // Mirrors the legacy editor's channel change, which recreates the condition:
  // the message resets to the channel's wildcard, the event type to the
  // channel's default, and the refinements to theirs.
  const handleChannelChange = (value: string | null) => {
    if (value == null || value === channel || messageChannelOf(value) == null) {
      return;
    }
    const eventName = anyMessageValue(value);
    patch({
      eventName,
      eventType: defaultEventTypeFor(value, eventName, { emailOpenTrackingEnabled }),
      human: false,
      inverse: false,
    });
  };

  const handleMessageChange = (eventName: string) => {
    const partial: Partial<typeof state> = { eventName };
    // Switching between a regular in-app message and an anonymous broadcast
    // swaps the event-type vocabulary (actions vs content); snap to the new
    // list's first option when the current type no longer applies.
    const options = eventTypeOptionsFor(channel, eventName, state.eventType, { emailOpenTrackingEnabled });
    const currentIsValid = options.some((option) => option.value === state.eventType);
    if (!currentIsValid && options.length > 0) {
      partial.eventType = options[0]?.value;
    }
    patch(partial);
  };

  // Mirrors the legacy editor: picking an open or click metric on email
  // defaults the refinement to human-only.
  const handleEventTypeChange = (value: string | null) => {
    if (value == null) {
      return;
    }
    patch({ eventType: value, human: channel === 'email' && isOpenOrClickMetric(value) });
  };

  return (
    <InlineStack data-testid="dryad-condition-row" gap="050" alignBlock="baseline" wrap>
      {typeSelect}
      <Select
        data-testid="dryad-message-channel-select"
        ariaLabel="Message type"
        size="sm"
        options={channelOptions}
        value={channel}
        onChange={handleChannelChange}
        isDisabled={meta.isDisabled}
      />
      <MessageSelect
        channel={channel}
        value={state.eventName}
        allowInAppBroadcasts={allowInAppBroadcasts}
        disabled={meta.isDisabled}
        error={messageErrorMessage}
        onChange={handleMessageChange}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setMessageTouched(true);
          }
        }}
      />
      <Select
        data-testid="dryad-message-inverse-select"
        ariaLabel="Action inverse"
        size="sm"
        options={INVERSE_OPTIONS}
        value={state.inverse ? 'true' : 'false'}
        onChange={(value: string | null) => {
          if (value != null) {
            patch({ inverse: value === 'true' });
          }
        }}
        isDisabled={meta.isDisabled}
      />
      <Select
        data-testid="dryad-message-event-type-select"
        ariaLabel="Action event type"
        size="sm"
        options={eventTypeOptions}
        value={state.eventType}
        onChange={handleEventTypeChange}
        isDisabled={meta.isDisabled}
      />
      {showHumanMetricSelect ? (
        <Select
          data-testid="dryad-message-human-metric-select"
          ariaLabel="Human or machine metric"
          size="sm"
          options={HUMAN_METRIC_OPTIONS}
          value={state.human ? 'human' : 'human_or_machine'}
          onChange={(value: string | null) => {
            if (value != null) {
              patch({ human: value === 'human' });
            }
          }}
          isDisabled={meta.isDisabled}
        />
      ) : null}
    </InlineStack>
  );
}
